import React, { FunctionComponent } from "react";
import { createStyles, makeStyles } from "@mui/styles";
import { Card, CardMedia, CardActions, IconButton, CardContent, Typography, Link } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import { BookViewCardModelProps } from "../../models";
import { DateTimeHelper } from "../../helpers";

const useStyles = makeStyles(() =>
    createStyles({
        iconRow: {
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            margin: "16px 0"
        },
        iconImage: {
            width: "18%"
        },
        iconImageGrey: {
            width: "18%",
            cursor: "pointer",
            filter: "grayscale(1)"
        },
        done: {
            color: "green !important"
        }
    })
);

const BookViewCard: FunctionComponent<BookViewCardModelProps> = ({ userId, book, setEditMode, updateBook, getBookOverview }) => {
    const classes = useStyles();

    const toggleDone = async () => {
        const newBook = {
            ...book,
            doneDate: new Date().toISOString().split("T")[0],
            isDone: !book.isDone
        };
        await updateBook(userId, newBook, book.id ?? "");
        getBookOverview(userId);
    };

    const toggleStarted = async () => {
        const newBook = {
            ...book,
            startDate: new Date().toISOString().split("T")[0],
            hasStarted: !book.hasStarted
        };
        await updateBook(userId, newBook, book.id ?? "");
        getBookOverview(userId);
    };

    return (
        <Card
            variant="outlined"
            sx={{ paddingBottom: "4px", width: "100%", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
            {book.poster !== "" && (
                <CardMedia
                    sx={{
                        height: {
                            xs: "100%",
                            md: "380px"
                        }
                    }}
                    component="img"
                    image={book.poster}
                    alt="activity image"
                />
            )}
            <CardContent sx={{ padding: "16px 16px 0" }}>
                <Typography
                    variant="h6"
                    sx={{ lineHeight: "1.2" }}
                    color="text.secondary">
                    {book.name}
                </Typography>
                {book.duration && (
                    <Typography
                        variant="body2"
                        color="text.secondary">
                        {book.duration} Seiten
                    </Typography>
                )}
                {book.releaseDate && (
                    <Typography
                        variant="body2"
                        sx={{ marginBottom: "8px" }}
                        color="text.secondary">
                        {DateTimeHelper.toDateDisplay(book.releaseDate)}
                    </Typography>
                )}
                {book.hasStarted && !book.isDone && book.startDate && (
                    <Typography
                        variant="body2"
                        sx={{ marginBottom: "8px" }}
                        color="text.secondary">
                        Gestartet: {DateTimeHelper.toDateDisplay(book.startDate)}
                    </Typography>
                )}
                {book.isDone && book.doneDate && book.startDate && (
                    <Typography
                        variant="body2"
                        color="text.secondary">
                        Lesezeit: {DateTimeHelper.dateDiff(book.doneDate, book.startDate)}
                    </Typography>
                )}
                {book.isDone && book.doneDate && (
                    <Typography
                        variant="body2"
                        sx={{ marginBottom: "8px" }}
                        color="text.secondary">
                        Beendet: {DateTimeHelper.toDateDisplay(book.doneDate)}
                    </Typography>
                )}
                <Typography
                    variant="body2"
                    sx={{ marginBottom: "8px" }}
                    color="text.secondary"></Typography>
                <Typography
                    variant="body1"
                    sx={{ marginBottom: "8px", lineHeight: "1" }}
                    color="text.secondary">
                    {book.genre}
                </Typography>
                {book.linkGoodreads && (
                    <Link
                        href={book.linkGoodreads}
                        target="_blank"
                        variant="body2">
                        {"goodreads.com"}
                    </Link>
                )}
                {(book.isOnleihe || book.isLibrary) && (
                    <div className={classes.iconRow}>
                        {book.isLibrary && (
                            <img
                                className={classes.iconImage}
                                src="../assets/library.png"></img>
                        )}
                        {book.isOnleihe && (
                            <img
                                className={classes.iconImage}
                                src="../assets/onleihe.png"></img>
                        )}
                    </div>
                )}
            </CardContent>
            <CardActions
                sx={{ padding: "0 8px 8px" }}
                disableSpacing>
                <IconButton
                    onClick={() => setEditMode(book.id ?? "", true)}
                    aria-label="edit">
                    <EditIcon />
                </IconButton>
                <IconButton
                    className={book.isDone ? classes.done : ""}
                    onClick={toggleDone}
                    aria-label="done">
                    <CheckIcon />
                </IconButton>
                <IconButton
                    className={book.hasStarted ? classes.done : ""}
                    onClick={toggleStarted}
                    aria-label="start">
                    <PlayCircleIcon />
                </IconButton>
            </CardActions>
        </Card>
    );
};

export default BookViewCard;
